import React from 'react';
import { BookOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import remove from 'lodash/remove';

import IncidentStatusEnum from 'redpoints-model-js/enumeration/IncidentStatusEnum';

import { MoreInfo } from 'redpoints-front-components';
import { PriorityIcon } from 'redpoints-front-common/components';
import { cast } from 'redpoints-front-common/helpers';

import rpfdate from 'redpoints-front-date';

import { REPORT_TYPE } from './AllIncidents';
import { LinkStatus } from './AllIncidentsTableItem';
import EnumController from '../../../enumerations/EnumController';
import { translate } from '../../../helpers/languageHelper';

export const parentColumns = (handlers, tableActionsStatus, reportType) => {
  const columns = [
    {
      title: translate('allIncidents.tableHeader.id'),
      dataIndex: 'incidentId',
      key: 'incidentId',
      sorter: true,
      align: 'center',
      render: renderIncidentId,
    },
    {
      title: translate('allIncidents.tableHeader.asset'),
      dataIndex: 'assetName',
      key: 'assetName',
      sorter: true,
      align: 'center',
      render: renderAssetName,
    },
    {
      title: translate('allIncidents.tableHeader.protectionType'),
      dataIndex: 'protectionType',
      key: 'protectionType',
      sorter: true,
      align: 'center',
      render: renderProtectionType,
    },
    {
      title: translate('allIncidents.tableHeader.detectionDomain'),
      dataIndex: 'detectionDomain',
      key: 'detectionDomain',
      sorter: true,
      align: 'center',
    },
    {
      title: translate('allIncidents.tableHeader.detectionDate'),
      dataIndex: 'detectionDate',
      key: 'detectionDate',
      sorter: true,
      align: 'center',
      render: renderDate,
    },
    {
      title: translate('allIncidents.tableHeader.confirmationDate'),
      dataIndex: 'confirmationDate',
      key: 'confirmationDate',
      sorter: true,
      align: 'center',
      render: renderDate,
    },
    {
      title: translate('allIncidents.tableHeader.enforcementDate'),
      dataIndex: 'enforcementDate',
      key: 'enforcementDate',
      sorter: false,
      align: 'center',
      render: renderDate,
    },
    {
      title: translate('allIncidents.tableHeader.closureDate'),
      dataIndex: 'closureDate',
      key: 'closureDate',
      sorter: true,
      align: 'center',
      render: renderDate,
    },
    {
      title: translate('allIncidents.tableHeader.incidentStatus'),
      dataIndex: 'incidentStatus',
      key: 'incidentStatus',
      sorter: true,
      align: 'center',
      render: renderIncidentStatus,
    },
    {
      width: '60px',
      title: 'Label',
      key: 'label',
      render: renderRowIconActions(handlers, tableActionsStatus, reportType),
    },
  ];

  const result =
    reportType === REPORT_TYPE.INTERNAL
      ? [...columns]
      : remove(columns, column => column.key !== 'enforcementDate');

  return result;
};

export const childColumns = ({
  renderRowDeIndexation,
  renderRowLink,
  renderRowRejectedReason,
  reportType,
}) => {
  const columns = [
    {
      title: translate('allIncidents.linksTableHeader.link'),
      dataIndex: '_url',
      key: 'url',
      render: renderRowLink,
      width: '34%',
    },
    {
      title: translate('allIncidents.linksTableHeader.type'),
      dataIndex: ['_linkType', 'name'],
      key: 'linkType',
      width: '16%',
    },
    {
      title: translate('allIncidents.linksTableHeader.status'),
      key: 'enforcementStatus',
      render: (_, record) => (
        <LinkStatus
          enforcementStatusName={record._enforcementStatus?.name}
          reasonName={record._reason?.name}
        />
      ),
      width: '16%',
    },
    {
      title: translate('allIncidents.linksTableHeader.deindexation.column'),
      dataIndex: 'deindexation',
      key: 'deindexation',
      render: renderRowDeIndexation,
      width: '16%',
    },
    {
      title: translate(
        'allIncidents.linksTableHeader.deindexation.rejectedReason'
      ),
      dataIndex: 'deindexation',
      key: 'rejectedReason',
      render: renderRowRejectedReason,
      width: '16%',
    },
  ];

  const result =
    reportType === REPORT_TYPE.INTERNAL
      ? [...columns]
      : remove(columns, column => column.key !== 'operation');

  return result;
};

const renderIncidentId = (_, { incidentId, assetId }) => (
  <a
    target="_blank"
    rel="noopener noreferrer"
    href={`assets/${assetId}/incidents/${incidentId}`}
  >
    {incidentId}
  </a>
);

const renderDate = date =>
  date ? (
    <span>
      {cast.dateToString(rpfdate(date))}
      <br />
      {cast.dateToString(rpfdate(date), rpfdate.MASKS.TIME)}
    </span>
  ) : (
    '-'
  );

const renderRowIconActions = (
  { handleWatchingClick },
  { tableActionsBookmarkStatus }
) => (_, record) => {
  const { incidentId, priority } = record;
  const prioritySize = 16;
  const priorityFontSize = 12.8;

  return (
    <div className="icons-container">
      <PriorityIcon
        fontSize={priorityFontSize}
        priority={priority}
        size={prioritySize}
      />
      <Button
        aria-label="watchingIncidenceBtn"
        className={
          record.watching ? 'action-icons danger-color' : 'action-icons'
        }
        disabled={tableActionsBookmarkStatus(incidentId)}
        icon={<BookOutlined />}
        onClick={handleWatchingClick(record)}
        shape="text"
        size="small"
      />
    </div>
  );
};

const renderAssetName = asset => (
  <span className="asset-name-text">{asset}</span>
);

const renderProtectionType = type =>
  translate(`global.protectiontype.${type.name}`);

const renderIncidentStatus = (incidentStatus, record) => {
  const isIncidentDiscarded =
    incidentStatus.name === IncidentStatusEnum.INCIDENT_DISCARDED.name;

  const { incidentDiscardedReason } = record;

  const statusTranslation = translate(
    EnumController.getLiteralKey(IncidentStatusEnum, incidentStatus.name)
  );

  const shouldRenderMoreInfo = isIncidentDiscarded && incidentDiscardedReason;

  return (
    <>
      {statusTranslation}
      {shouldRenderMoreInfo && (
        <>
          {' '}
          <MoreInfo content={incidentDiscardedReason} />
        </>
      )}
    </>
  );
};
